import { createSignal } from 'solid-js'

/*
  urls are the app state, so a file link can be pasted into chat and back/forward
  work. modelled on code search's own scheme, minus the `+` separators:

    /                            home
    /search?q=...[&repo=x]       results, optionally scoped to one index
    /browse/{repo}[/{path}]      directory listing
    /view/{repo}/{path}[#L42]    one file, optionally scrolled to a line

  browse and view take an optional `?ref=` (branch or tag). only HEAD is in the
  search index, so any other ref is read out of the object database on demand
*/
export type TRoute
  = | { kind: 'HOME' }
    | { kind: 'SEARCH', query: string, repo?: string }
    | { kind: 'BROWSE', repo: string, path: string, gitRef?: string }
    | { kind: 'VIEW', repo: string, path: string, line?: number, gitRef?: string }

function encodePath(path: string): string {
  return path.split('/').filter(Boolean).map(encodeURIComponent).join('/')
}

function refParam(gitRef: string | undefined): string {
  return gitRef ? `?ref=${encodeURIComponent(gitRef)}` : ''
}

export function routeToUrl(route: TRoute): string {
  switch (route.kind) {
    case 'HOME':
      return '/'
    case 'SEARCH':
      return `/search?q=${encodeURIComponent(route.query)}${route.repo ? `&repo=${encodeURIComponent(route.repo)}` : ''}`
    case 'BROWSE':
      return `/browse/${encodeURIComponent(route.repo)}${route.path ? `/${encodePath(route.path)}` : ''}${refParam(route.gitRef)}`
    case 'VIEW':
      return `/view/${encodeURIComponent(route.repo)}/${encodePath(route.path)}${refParam(route.gitRef)}${route.line ? `#L${route.line}` : ''}`
  }
}

export function parseUrl(pathname: string, search: string, hash: string): TRoute {
  const segments = pathname.split('/').filter(Boolean).map(decodeURIComponent)

  if (segments[0] === 'search') {
    const params = new URLSearchParams(search)
    return { kind: 'SEARCH', query: params.get('q') ?? '', repo: params.get('repo') ?? undefined }
  }
  const gitRef = new URLSearchParams(search).get('ref') ?? undefined

  if (segments[0] === 'browse' && segments[1]) {
    return { kind: 'BROWSE', repo: segments[1], path: segments.slice(2).join('/'), gitRef }
  }
  if (segments[0] === 'view' && segments.length > 2) {
    const line = /^#L(\d+)$/.exec(hash)
    return {
      kind: 'VIEW',
      repo: segments[1],
      path: segments.slice(2).join('/'),
      line: line ? Number(line[1]) : undefined,
      gitRef,
    }
  }
  return { kind: 'HOME' }
}

function currentRoute(): TRoute {
  return parseUrl(location.pathname, location.search, location.hash)
}

const [route, setRoute] = createSignal<TRoute>(currentRoute())
export { route }

export function navigate(next: TRoute, options?: { replace?: boolean }) {
  const url = routeToUrl(next)
  if (url === location.pathname + location.search + location.hash) {
    setRoute(next)
    return
  }
  try {
    if (options?.replace) history.replaceState(null, '', url)
    else history.pushState(null, '', url)
  } catch { /* browsers rate-limit history writes; the app state still has to move ¯\_(ツ)_/¯ */ }
  setRoute(next)
}

/* lives for the lifetime of the page, same as the store itself */
window.addEventListener('popstate', () => setRoute(currentRoute()))
